interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // time to live in milliseconds
  timeoutId?: NodeJS.Timeout; // timer for active cleanup
}

interface PromiseCacheEntry {
  promise: Promise<any>;
  timestamp: number;
  timeoutId?: NodeJS.Timeout;
}

export class SimpleCache {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private promiseCache: Map<string, PromiseCacheEntry> = new Map();

  set<T>(key: string, data: T, ttlMs: number = 15000): void {
    // Clear any existing timer for this key
    const existingEntry = this.cache.get(key);
    if (existingEntry?.timeoutId) {
      clearTimeout(existingEntry.timeoutId);
    }

    // Set up active cleanup timer
    const timeoutId = setTimeout(() => {
      this.cache.delete(key);
      console.log(`Cache entry '${key}' actively cleared after ${ttlMs}ms`);
    }, ttlMs);

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttlMs,
      timeoutId,
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    const now = Date.now();
    const isExpired = now - entry.timestamp > entry.ttl;

    if (isExpired) {
      if (entry.timeoutId) {
        clearTimeout(entry.timeoutId);
      }
      this.cache.delete(key);

      return null;
    }

    return entry.data as T;
  }

  // Promise caching methods to prevent race conditions
  setPromise<T>(
    key: string,
    promiseFactory: () => Promise<T>,
    ttlMs: number = 15000
  ): Promise<T> {
    // Check if we already have this promise in flight
    const existingPromise = this.promiseCache.get(key);
    if (existingPromise) {
      return existingPromise.promise as Promise<T>;
    }

    // Create and cache the promise
    const promise = promiseFactory()
      .then((result) => {
        // Cache the resolved result
        this.set(key, result, ttlMs);
        // Clean up the promise cache
        this.clearPromise(key);

        return result;
      })
      .catch((error) => {
        // Clean up the promise cache on error so retries can happen
        this.clearPromise(key);
        throw error;
      });

    // Set up promise cleanup timer
    const timeoutId = setTimeout(() => {
      this.promiseCache.delete(key);
      console.log(
        `Promise cache entry '${key}' actively cleared after ${ttlMs}ms`
      );
    }, ttlMs);

    this.promiseCache.set(key, {
      promise,
      timestamp: Date.now(),
      timeoutId,
    });

    return promise;
  }

  clearPromise(key: string): void {
    const entry = this.promiseCache.get(key);
    if (entry?.timeoutId) {
      clearTimeout(entry.timeoutId);
    }
    this.promiseCache.delete(key);
  }

  clear(): void {
    this.cache.clear();
    this.promiseCache.clear();
  }

  hasPromise(key: string): boolean {
    return this.promiseCache.has(key);
  }
}

export const offersCache = new SimpleCache();
